import axios from 'axios';
import { 
  User, 
  Transaction, 
  Category, 
  CreateTransactionRequest, 
  UpdateTransactionRequest,
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  AICategorizationRequest,
  AICategorizationResponse,
  TransactionFilters,
  BudgetPeriod,
  CreateBudgetPeriodRequest,
  UpdateBudgetPeriodRequest,
  BudgetAllocation,
  CreateBudgetAllocationRequest,
  UpdateBudgetAllocationRequest,
  EnvelopeBudgetSummary,
  BudgetCarryoverRequest,
  BudgetCarryoverResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authApi = {
  register: async (data: RegisterRequest): Promise<User> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const formData = new URLSearchParams();
    formData.append('username', data.username);
    formData.append('password', data.password);
    
    const response = await api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },
  
  getMe: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Transactions API
export const transactionsApi = {
  getTransactions: async (filters: TransactionFilters = {}): Promise<Transaction[]> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/transactions/?${params}`);
    return response.data;
  },
  
  getTransaction: async (id: number): Promise<Transaction> => {
    const response = await api.get(`/transactions/${id}/`);
    return response.data;
  },
  
  createTransaction: async (data: CreateTransactionRequest): Promise<Transaction> => {
    const response = await api.post('/transactions/', data);
    return response.data;
  },
  
  updateTransaction: async (id: number, data: UpdateTransactionRequest): Promise<Transaction> => {
    const response = await api.put(`/transactions/${id}`, data);
    return response.data;
  },
  
  deleteTransaction: async (id: number): Promise<void> => {
    await api.delete(`/transactions/${id}`);
  },
  
  uploadCSV: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/transactions/upload-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Categories API
export const categoriesApi = {
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/categories/');
    return response.data;
  },
  
  createCategory: async (data: Omit<Category, 'id' | 'is_default'>): Promise<Category> => {
    const response = await api.post('/categories/', data);
    return response.data;
  },
  
  updateCategory: async (id: number, data: Omit<Category, 'id' | 'is_default'>): Promise<Category> => {
    const response = await api.put(`/categories/${id}/`, data);
    return response.data;
  },
  
  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/categories/${id}/`);
  },
};

// AI API
export const aiApi = {
  categorizeTransaction: async (data: AICategorizationRequest): Promise<AICategorizationResponse> => {
    const response = await api.post('/ai/categorize/', data);
    return response.data;
  },
  
  batchCategorize: async (transactionIds: number[]): Promise<any> => {
    const response = await api.post('/ai/categorize-batch/', { transaction_ids: transactionIds });
    return response.data;
  },
  
  learnCorrection: async (transactionId: number, correctCategoryId: number): Promise<void> => {
    await api.post('/ai/learn-correction/', {
      transaction_id: transactionId,
      correct_category_id: correctCategoryId,
    });
  },
};

// Analytics API
export const analyticsApi = {
  getMonthOverMonth: async (year?: number): Promise<any> => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/analytics/month-over-month${params}`);
    return response.data;
  },
  
  getYearOverYear: async (): Promise<any> => {
    const response = await api.get('/analytics/year-over-year');
    return response.data;
  },
  
  getTopCategoriesYTD: async (limit: number = 3): Promise<any> => {
    const response = await api.get(`/analytics/top-categories-ytd?limit=${limit}`);
    return response.data;
  },
  
  getSpendingTrends: async (): Promise<any> => {
    const response = await api.get('/analytics/spending-trends');
    return response.data;
  },

  getMonthlyCashFlow: async (months: number = 12): Promise<any> => {
    const response = await api.get(`/analytics/monthly-cash-flow?months=${months}`);
    return response.data;
  },

  getCategoryComparison: async (): Promise<any> => {
    const response = await api.get('/analytics/category-comparison');
    return response.data;
  },

  getWeeklySpendingPatterns: async (): Promise<any> => {
    const response = await api.get('/analytics/weekly-spending-patterns');
    return response.data;
  },

  getCategoryTrends: async (months: number = 6, limit: number = 5): Promise<any> => {
    const response = await api.get(`/analytics/category-trends?months=${months}&limit=${limit}`);
    return response.data;
  },

  getBudgetVsActual: async (month?: number, year?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    const response = await api.get(`/analytics/budget-vs-actual?${params}`);
    return response.data;
  },

  getIncomeStatement: async (): Promise<any> => {
    const response = await api.get('/analytics/income-statement');
    return response.data;
  },

  getSavingsMetrics: async (): Promise<any> => {
    const response = await api.get('/analytics/savings-metrics');
    return response.data;
  },
};


// Envelope Budgets API
export const envelopeBudgetsApi = {
  // Budget Periods
  getBudgetPeriods: async (): Promise<BudgetPeriod[]> => {
    const response = await api.get('/envelope-budgets/periods');
    return response.data;
  },

  getBudgetPeriod: async (year: number, month: number): Promise<BudgetPeriod> => {
    const response = await api.get(`/envelope-budgets/periods/${year}/${month}`);
    return response.data;
  },

  createBudgetPeriod: async (data: CreateBudgetPeriodRequest): Promise<BudgetPeriod> => {
    const response = await api.post('/envelope-budgets/periods', data);
    return response.data;
  },

  updateBudgetPeriod: async (periodId: number, data: UpdateBudgetPeriodRequest): Promise<BudgetPeriod> => {
    const response = await api.put(`/envelope-budgets/periods/${periodId}`, data);
    return response.data;
  },

  // Budget Allocations
  createAllocation: async (periodId: number, data: CreateBudgetAllocationRequest): Promise<BudgetAllocation> => {
    const response = await api.post(`/envelope-budgets/periods/${periodId}/allocations`, data);
    return response.data;
  },

  updateAllocation: async (allocationId: number, data: UpdateBudgetAllocationRequest): Promise<BudgetAllocation> => {
    const response = await api.put(`/envelope-budgets/allocations/${allocationId}`, data);
    return response.data;
  },

  deleteAllocation: async (allocationId: number): Promise<void> => {
    await api.delete(`/envelope-budgets/allocations/${allocationId}`);
  },

  // Summary & Utilities
  getEnvelopeBudgetSummary: async (year: number, month: number): Promise<EnvelopeBudgetSummary> => {
    const response = await api.get(`/envelope-budgets/summary/${year}/${month}`);
    return response.data;
  },

  refreshSpentAmounts: async (year: number, month: number): Promise<void> => {
    await api.post(`/envelope-budgets/refresh-spent-amounts/${year}/${month}`);
  },

  // Carryover
  carryoverBudget: async (data: BudgetCarryoverRequest): Promise<BudgetCarryoverResponse> => {
    const response = await api.post('/envelope-budgets/carryover', data);
    return response.data;
  },
};

export default api;
